import { useEffect, useMemo, useState } from 'react';
import {
  MCP_SCOPE_DESCRIPTION,
  type McpScope,
  type PendingAuthorization,
} from '@allemnii-pulse/shared';
import { api, ApiError } from '../../lib/api.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../../components/ui/Button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { Checkbox } from '../../components/ui/Checkbox.js';

/**
 * The consent screen for an MCP client — the one moment a human decides what an AI agent may do
 * with their Pulse account.
 *
 * Two things drive the design. First, clients register themselves (open dynamic registration is
 * what makes `claude mcp add <url>` a single step), so the name on this page is attacker-supplied
 * text and is presented as a claim, not a fact. The redirect address is shown next to it because
 * that is the part nobody can forge: it is where the authorization code is actually delivered, so
 * a loopback address means the grant is landing on this machine and anything else is worth
 * pausing over.
 *
 * Second, the write permissions are unticked-able. An agent that only reads is a strictly safer
 * thing to connect, and a user who wants that should not have to decline the whole request to get
 * it. The server intersects whatever is returned with what was requested, so this can only ever
 * narrow the grant.
 */

/** Read access is what makes the connector useful at all; unticking it would grant an agent that
 * can write but cannot see what it is writing about. Kept visible, and fixed. */
const ALWAYS_GRANTED: McpScope[] = ['context:read', 'tasks:read'];

export function AuthorizeAppPage() {
  const { user, signOutUser } = useAuth();
  const requestId = useMemo(() => new URLSearchParams(window.location.search).get('request'), []);

  const [request, setRequest] = useState<PendingAuthorization | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<McpScope>>(new Set());
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);

  useEffect(() => {
    if (!requestId) {
      setError('This link is missing its authorization request. Start again from your terminal.');
      return;
    }
    api
      .get<PendingAuthorization>(`/oauth/requests/${encodeURIComponent(requestId)}`)
      .then((r) => {
        setRequest(r);
        setSelected(new Set(r.scopes));
      })
      .catch((err) =>
        setError(
          err instanceof ApiError
            ? err.message
            : 'Could not load this authorization request. Start again from your terminal.',
        ),
      );
  }, [requestId]);

  async function decide(action: 'approve' | 'deny') {
    if (!request) return;
    setBusy(action);
    try {
      const body = action === 'approve' ? { scopes: [...selected] } : undefined;
      const { redirectTo } = await api.post<{ redirectTo: string }>(
        `/oauth/requests/${encodeURIComponent(request.requestId)}/${action}`,
        body,
      );
      // replace(), not assign(): the consent request is spent, so leaving it in history would
      // only offer the user a back button onto an expired page.
      window.location.replace(redirectTo);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Start again from your terminal.',
      );
      setBusy(null);
    }
  }

  if (error) {
    return (
      <Shell>
        <Card>
          <CardHeader>
            <CardTitle>Authorization failed</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted">Nothing was authorized.</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (!request) {
    return (
      <Shell>
        <p className="text-sm text-muted">Loading…</p>
      </Shell>
    );
  }

  const isLoopback = /^http:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/.test(request.redirectUri);
  const writeScopes = request.scopes.filter((s) => !ALWAYS_GRANTED.includes(s));

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle className="leading-snug wrap-break-word">
            {request.isReauthorization ? 'Reconnect' : 'Connect'} “{request.clientName}” to Allemnii
            Pulse?
          </CardTitle>
          <CardDescription>
            It will act as <span className="font-medium text-fg wrap-anywhere">{user?.email}</span>,
            and can only reach projects you are already a member of.
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <span className="text-xs font-medium uppercase tracking-wide text-muted">
              This app will be able to
            </span>
            {ALWAYS_GRANTED.filter((s) => request.scopes.includes(s)).map((scope) => (
              <label key={scope} className="flex items-start gap-3">
                <Checkbox checked disabled aria-label={scope} />
                <span className="text-sm leading-snug">{MCP_SCOPE_DESCRIPTION[scope]}</span>
              </label>
            ))}
            {writeScopes.map((scope) => (
              <label key={scope} className="flex items-start gap-3">
                <Checkbox
                  checked={selected.has(scope)}
                  onCheckedChange={(v) =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (v === true) next.add(scope);
                      else next.delete(scope);
                      return next;
                    })
                  }
                  aria-label={scope}
                />
                <span className="text-sm leading-snug">
                  {MCP_SCOPE_DESCRIPTION[scope]}{' '}
                  <span className="text-muted">
                    Optional — untick to connect with read-only access.
                  </span>
                </span>
              </label>
            ))}
          </div>

          <div className="rounded-card border border-border bg-bg p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted">
              Access will be sent to
            </p>
            <p className="mt-1 break-all font-mono text-xs">{request.redirectUri}</p>
            <p className="mt-2 text-xs text-muted">
              {isLoopback
                ? 'That is an address on your own computer, which is what a local tool like Claude Code uses.'
                : 'This is a remote address, not your own computer. Only continue if you recognize it — the app’s ' +
                  'name is chosen by whoever registered it and proves nothing on its own.'}
            </p>
          </div>

          <p className="text-xs text-muted">
            It can never mark work Done or In review, edit your PRD or TDD, assign tasks to other
            people, or change who is on a project. Reporting a blocker or adding a deliverable will
            ask you first, every time. You can disconnect it at any point from Settings.
          </p>
        </CardContent>

        <CardFooter className="justify-end gap-2">
          <Button
            variant="outline"
            className="h-10 sm:h-8"
            disabled={busy !== null}
            onClick={() => void decide('deny')}
          >
            {busy === 'deny' ? 'Cancelling…' : 'Cancel'}
          </Button>
          <Button
            className="h-10 sm:h-8"
            disabled={busy !== null || selected.size === 0}
            onClick={() => void decide('approve')}
          >
            {busy === 'approve' ? 'Authorizing…' : 'Authorize'}
          </Button>
        </CardFooter>
      </Card>

      <p className="text-center text-xs text-muted wrap-anywhere">
        Signed in as {user?.email}.{' '}
        <button className="underline underline-offset-2" onClick={() => void signOutUser()}>
          Use a different account
        </button>
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-4 sm:p-6">
      <div className="flex w-full max-w-md flex-col gap-4">{children}</div>
    </div>
  );
}
